/**
 * Pre-Init Acquire Buffer · T7130.
 *
 * Deep-link cold-start ordering: Expo / Next bootstrap → navigation
 * container mount → SDK init() → first SdkScreen mount whose hooks call
 * `acquireDomain` / `acquireWhenVisible`. Steps 3 and 4 race; on a fast
 * device the screen mounts BEFORE the JWT mint resolves.
 *
 * Registry MUST accept acquire calls before init completes. Pre-init
 * acquires queue in a bounded buffer (cap 4096 entries). Overflow is
 * priority-based:
 *   - drop 'shell' first (layout-mounted hooks)
 *   - if buffer is screen-only, drop the oldest 'screen' entry
 * Dropped acquires reject with `SdkError('PRE_INIT_BUFFER_FULL')`.
 *
 * On first successful CONNECT ack the buffer drains in insertion order;
 * each retained entry replays through its execute fn.
 */

import { makeSdkError } from '../../errors/SdkError.js';

export type Priority = 'screen' | 'shell';

export type AcquireKind = 'domain' | 'entity' | 'visible';

export interface QueuedAcquire {
  readonly kind: AcquireKind;
  readonly priority: Priority;
  readonly insertSeq: number;
  readonly args: unknown;
  resolve(release: () => void): void;
  reject(err: unknown): void;
}

/** Spec cap. Exported for tests that drive overflow without filling 4096. */
export const PREINIT_BUFFER_CAP = 4_096;

export class PreInitAcquireBuffer {
  private buf: QueuedAcquire[] = [];
  private seq = 0;
  private readonly cap: number;

  constructor(cap: number = PREINIT_BUFFER_CAP) {
    this.cap = cap;
  }

  enqueue(q: Omit<QueuedAcquire, 'insertSeq'>): void {
    if (this.buf.length >= this.cap) {
      // Drop shell first; otherwise oldest screen.
      const shellIdx = this.buf.findIndex((e) => e.priority === 'shell');
      let dropped: QueuedAcquire | undefined;
      if (shellIdx >= 0) {
        const spliced = this.buf.splice(shellIdx, 1);
        dropped = spliced[0];
      } else {
        dropped = this.buf.shift();
      }
      if (dropped !== undefined) {
        dropped.reject(
          makeSdkError({
            category: 'rate-limited',
            code: 'PRE_INIT_BUFFER_FULL',
            userMessage: 'errors.preinit_buffer_full',
            devMessage: `Pre-init acquire buffer overflowed (cap ${this.cap})`,
            correlationId: '',
            retryAfterMs: 0,
          }),
        );
      }
    }
    this.buf.push({ ...q, insertSeq: this.seq++ });
  }

  drain(executeAcquire: (q: QueuedAcquire) => () => void): void {
    while (this.buf.length > 0) {
      const q = this.buf.shift();
      if (q === undefined) break;
      try {
        const release = executeAcquire(q);
        q.resolve(release);
      } catch (err) {
        q.reject(err);
      }
    }
  }

  size(): number {
    return this.buf.length;
  }

  /** Test/debug introspection — copy of the underlying queue. */
  snapshot(): readonly QueuedAcquire[] {
    return [...this.buf];
  }
}
